import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Shop } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import { DashboardWelcomeHeader, EmptyState, SefanoButton } from "@/components/Molecules";
import {
  DashboardStatsRow,
  LatestOrdersCard,
  OrderTrendChart,
  QuickOrderHero,
  CommissionLockedState,
} from "@/components/Organisms";
import type { HomeTemplateProps } from "./Home.template.types";
import * as styles from "./Home.template.styles";

export type {
  HomeLatestOrdersProps,
  HomeStatsProps,
  HomeCommissionLock,
  HomeTemplateProps,
  HomeTrendProps,
  HomeWelcomeProps,
} from "./Home.template.types";

function DashboardNoShopView() {
  const { t } = useTranslation("home");
  const theme = useTheme();

  return (
    <EmptyState
      icon={<Shop size={56} variant="Linear" color={theme.palette.text.disabled} />}
      title={t("dashboard.noShop.title")}
      description={t("dashboard.noShop.description")}
      action={
        <Link to="/shops/create">
          <SefanoButton intent="add">{t("dashboard.noShop.createShop")}</SefanoButton>
        </Link>
      }
    />
  );
}

export function HomeTemplate({
  hasNoShop,
  commissionLock,
  welcome,
  quickOrderSectionId,
  quickOrder,
  stats,
  latestOrders,
  trend,
}: HomeTemplateProps) {
  return (
    <Box sx={styles.main}>
      <Box sx={styles.content}>
        {hasNoShop ? (
          <DashboardNoShopView />
        ) : (
          <>
            <DashboardWelcomeHeader
              storeName={welcome.storeName}
              date={welcome.date}
              todaySales={welcome.todaySales}
              awaitingPayment={welcome.awaitingPayment}
            />

            <Box id={quickOrderSectionId} sx={styles.quickOrderSection}>
              {commissionLock.isOrderCreateBlocked ? (
                <CommissionLockedState canPayDebt={commissionLock.canPayDebt} compact />
              ) : (
                <QuickOrderHero {...quickOrder} />
              )}
            </Box>

            <DashboardStatsRow
              todaySales={stats.todaySales}
              awaitingPayment={stats.awaitingPayment}
              preparing={stats.preparing}
              isLoading={stats.isLoading}
            />

            <Box sx={styles.columns}>
              <Box sx={styles.leftColumn}>
                <LatestOrdersCard
                  orders={latestOrders.orders}
                  isLoading={latestOrders.isLoading}
                  isError={latestOrders.isError}
                  onCreateFirstClick={latestOrders.onCreateFirstClick}
                />
              </Box>

              <Box sx={styles.rightColumn}>
                <OrderTrendChart
                  data={trend.data}
                  growthPercent={trend.growthPercent}
                  isLoading={trend.isLoading}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
